import { COLOR } from "../utils/enum";
import { getDiffDateInSec, getEventName } from "../utils/utils";

const DEFAULT_MARKER_ICON_URL = `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/point-of-interest.png`;

const getMarkerIconUrl = (properties) => {
    const type = properties?.type;
    if (type &&(type.name === 'information' || type.name === 'structure_medicale') && properties.cat) {
        return `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/${properties.cat}.png`;
    }
    if (type && type.icon) {
        return `${process.env.GENDLOC_BACKEND_URL}${type.icon}`;
    }
    if (properties.operateur) { //FREE MOBILE -> FREEMOBILE, BOUYGUES TELECOM -> BOUYGUESTELECOM...
        return `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/${properties.operateur.replace(/\s+/g, '')}.png`;
    }

    return DEFAULT_MARKER_ICON_URL;
};

/**
 * Ensemble d'extensions de Marker 
 * avec une utilisée par défaut
 */
const defaultMarker = L.Marker.extend({
    initialize: function (latlng, options) {
        L.Marker.prototype.initialize.call(this, latlng, options);
        this._properties = options?.properties || null;
        this._featureId = options?.featureId || null;

        const iconUrl = getMarkerIconUrl(this._properties);
        this.setIcon(L.icon({ iconUrl, iconSize: [40,40], iconAnchor: [20, 20] }));
        this.bindPopup(L.overlayPopup(this._properties, {
                latlng: latlng, featureId: this._featureId
            }));

        if (this._properties?.azimut != null) {
            const rotationAngle = Number(this._properties.azimut);
            if (Number.isFinite(rotationAngle)) {
                this.setRotationAngle(rotationAngle);
            }
            this.setIcon(L.icon({ iconUrl, iconSize: [100,70] }));
        }

        if (this._properties?.type?.name === 'gendloc') {
            const accuracy = parseFloat(this._properties.accuracy);
            if (!isNaN(accuracy)) {
                let iconSize, opacity;
                if (accuracy > 30) {
                    iconSize = [60, 60];
                    opacity = 0.3;
                } else if (accuracy < 15) {
                    iconSize = [18, 18];
                    opacity = 0.9;
                } else {
                    iconSize = [30, 30];
                    opacity = 0.5;
                }
                this.setOpacity(opacity);
                this.setIcon(L.icon({ iconUrl, iconSize, iconAnchor: [iconSize[0]/2, iconSize[1]/2] }));
            }
        }
    }
});

const eventMarker = defaultMarker.extend({
    initialize: function (latlng, options) {
        defaultMarker.prototype.initialize.call(this, latlng, options);

        let color = (this._properties.as_partner_squad && (!this._properties.as_support_squad && !this._properties.as_helicopter_squad)) ?  'blue' : (COLOR[this._properties.last_status] ?? 'red');
        if (!this._properties.open) {
            color = 'gray';
        }

        const displayId = this._properties.event_local_id;

        this.setIcon(new L.AwesomeNumberMarkers({
            number: displayId,
            markerColor: color
        }));

        const event = {
            created_at: this._properties.event_created_at,
            activity: this._properties.event_activity,
            local_id: displayId,
            com: this._properties.com,
        };
        
        this.bindTooltip(getEventName(event), { direction: 'top' });
        this._properties.name = getEventName(event);

        this.on('click', () => {
            const url = new URL(window.location.href);
            url.hash = '';
            url.search = new URLSearchParams({
                event: this._properties.id,
                lat: this.getLatLng().lat,
                lng: this.getLatLng().lng,
                zoom: 16
            }).toString();

            this.bindPopup(L.overlayPopup(this._properties, {
                urlDetails: url.toString(),
                isSupport: this._properties.as_support_squad,
                isOwner: this._properties.is_owner,
                isPartner: this._properties.as_partner_squad,
                isMed: this._properties.is_med,
                latlng: latlng,
                featureId: this._featureId,
                evt_id: this._properties.id
            })).openPopup();
        });
    },
});

const traccarMarker = L.Marker.extend({
    _device: {},
    _updateInterval: null,
    _currentIconUrl: null,

    initialize: function (latlng, options) {
        L.Marker.prototype.initialize.call(this, latlng, options);

        this._device = options;
        this._setIcon();
        this._setRotation();
    
        this.bindTooltip(options.name, { direction: 'top' });
        this.bindPopup(L.overlayPopup({...options, type: 'traccar'}, { latlng: latlng }));
    },

    refresh: function (latLng, device) {
        if (device) {
            this._device = device;
        }

        this.setLatLng(latLng);
        this._setIcon();
        this._setRotation();
        this.setPopupContent(Handlebars.templates['traccar-popup']({
            properties: this._device,
            latlng: latLng
        }));
    },

    _setIcon: function () {
        let iconUrl = `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/gps${this._device.status !== 'online' ? '_gris' : ''}.png`;
        let iconSize = [35, 35];
        let iconAnchor = [17, 17];

        if (this._device.name && this._device.name.includes('DRAGON')) {
            iconUrl = `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/${this._device.status === 'online' ? 'EC145_SC' : 'EC145_NB'}.png`;
            iconSize = [60, 60];
            iconAnchor = [30, 30];
        }

        if (this._device.name && this._device.name.includes('CHOUCAS')) {
            iconUrl = `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/${this._device.status === 'online' ? 'EC145_GIE' : 'EC145_NB'}.png`;
            iconSize = [60, 60];
            iconAnchor = [30, 30];
        }

        if (this._device.name && this._device.name.includes('YETI')) {
            iconUrl = `${process.env.GENDLOC_BACKEND_URL}/media/uploads/icons/${this._device.status === 'online' ? 'EC145' : 'EC145_NB'}.png`;
            iconSize = [60, 60];
            iconAnchor = [30, 30];
        }

        if (this._currentIconUrl !== iconUrl) {
            this.setIcon(L.icon({iconUrl, iconSize, iconAnchor}));
            this._currentIconUrl = iconUrl;
        }
    },

    _setRotation: function () {
        if (this._device.lastPosition && this._device.lastPosition.course != null) {
            const rotationAngle = Number(this._device.lastPosition.course);
            if (Number.isFinite(rotationAngle)) {
                this.setRotationAngle(rotationAngle+90);
            }
        }
    }
});

const gendlocMarkers = {
    defaultMaker: (latlng, options) => new defaultMarker(latlng, options),
    eventMarker: (latlng, options) => new eventMarker(latlng, options)
};

(() => {
    L.traccarMarker = (latlng, options) => new traccarMarker(latlng, options);

    // Factory function qui permet de charcher le bon marker en fonction du type
    L.gendlocMarker = (type, latlng, options) => {
        const cleanedType = type.toLowerCase()
            .replace(/[-_\s]+(.)?/g, (_, char) => char ? char.toUpperCase() : '');

        const markerName = `${cleanedType}Marker`;

        return markerName in gendlocMarkers
            ? gendlocMarkers[markerName](latlng, options)
            : new defaultMarker(latlng, options);
    };
})();
